import { supabase } from './client'
import { getPlatformAPI } from '../platform'
import { reportSupabaseAuthError } from './reportAuthError'
import type { AuthSession, AuthUser, OTPResult, VerifyOTPResult } from './types'

/**
 * Authentication Helper Functions
 * Handles user registration, login, logout, and session management
 */

// ==================== RATE LIMITING ====================

/**
 * Client-side rate limiter for auth endpoints.
 * Tracks failed attempts per action and enforces exponential backoff.
 * Resets on success.
 */
const MAX_ATTEMPTS = 8
const LOCKOUT_DURATION = 90 * 60 * 1000 // 90 minutes

const rateLimitState: Record<string, { failures: number; blockedUntil: number }> = {}

function checkRateLimit(action: string): string | null {
  const state = rateLimitState[action]
  if (!state) return null
  if (Date.now() < state.blockedUntil) {
    const msLeft = state.blockedUntil - Date.now()
    if (msLeft > 60000) {
      const minutesLeft = Math.ceil(msLeft / 60000)
      return `Too many attempts. Please wait ${minutesLeft} min.`
    }
    const secondsLeft = Math.ceil(msLeft / 1000)
    return `Too many attempts. Please wait ${secondsLeft}s.`
  }
  return null
}

function recordFailure(action: string): void {
  const state = rateLimitState[action] ?? { failures: 0, blockedUntil: 0 }
  state.failures++
  if (state.failures >= MAX_ATTEMPTS) {
    // Hard lockout after too many attempts
    state.blockedUntil = Date.now() + LOCKOUT_DURATION
  } else {
    // Exponential backoff: 2s, 4s, 8s, 16s, 30s max
    const delay = Math.min(2000 * Math.pow(2, state.failures - 1), 30000)
    state.blockedUntil = Date.now() + delay
  }
  rateLimitState[action] = state
}

function recordSuccess(action: string): void {
  delete rateLimitState[action]
}

/** Reset all rate limit state (for testing) */
export function resetRateLimits(): void {
  for (const key of Object.keys(rateLimitState)) {
    delete rateLimitState[key]
  }
}

// ==================== REGISTRATION ====================

export interface RegisterParams {
  email: string
  password: string
  fullName?: string
  language?: string
}

export interface RegisterResult {
  success: boolean
  user?: AuthUser
  error?: string
  requiresEmailVerification?: boolean
}

/**
 * Register a new user
 */
export async function registerUser(params: RegisterParams): Promise<RegisterResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  const rateLimitError = checkRateLimit('register')
  if (rateLimitError) return { success: false, error: rateLimitError }

  try {
    const { data, error } = await supabase.auth.signUp({
      email: params.email,
      password: params.password,
      options: {
        data: {
          full_name: params.fullName || '',
          ...(params.language
            ? { language: params.language.toLowerCase().split('-')[0] }
            : {}),
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      reportSupabaseAuthError(error, 'registerUser')
      recordFailure('register')
      return { success: false, error: error.message }
    }

    recordSuccess('register')

    if (!data.user) {
      return { success: false, error: 'Registration failed' }
    }

    // Check if email verification is required
    const requiresEmailVerification = !data.user.email_confirmed_at

    return {
      success: true,
      user: {
        id: data.user.id,
        email: data.user.email!,
        emailConfirmed: !!data.user.email_confirmed_at,
      },
      requiresEmailVerification,
    }
  } catch (error) {
    reportSupabaseAuthError(error, 'registerUser')
    return { success: false, error: `Registration error: ${error}` }
  }
}

// ==================== LOGIN ====================

export interface LoginParams {
  email: string
  password: string
}

export interface LoginResult {
  success: boolean
  user?: AuthUser
  session?: AuthSession
  error?: string
}

/**
 * Login user
 */
export async function loginUser(params: LoginParams): Promise<LoginResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  const rateLimitError = checkRateLimit('login')
  if (rateLimitError) return { success: false, error: rateLimitError }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: params.email,
      password: params.password,
    })

    if (error) {
      reportSupabaseAuthError(error, 'loginUser')
      recordFailure('login')
      return { success: false, error: error.message }
    }

    recordSuccess('login')

    if (!data.user || !data.session) {
      return { success: false, error: 'Login failed' }
    }

    // Cache full_name device-locally so the login screen can greet the user
    // by name on the next sign-out → sign-in cycle, even on a fresh device.
    const fullName = data.user.user_metadata?.full_name
    if (typeof fullName === 'string' && fullName.trim().length > 0) {
      try {
        localStorage.setItem('lastFullName', fullName)
      } catch {
        /* non-fatal */
      }
    }

    return {
      success: true,
      user: {
        id: data.user.id,
        email: data.user.email!,
        emailConfirmed: !!data.user.email_confirmed_at,
      },
      session: {
        accessToken: data.session.access_token,
        refreshToken: data.session.refresh_token,
        expiresAt: data.session.expires_at || 0,
        user: {
          id: data.user.id,
          email: data.user.email!,
          emailConfirmed: !!data.user.email_confirmed_at,
        },
      },
    }
  } catch (error) {
    reportSupabaseAuthError(error, 'loginUser')
    return { success: false, error: `Login error: ${error}` }
  }
}

// ==================== LOGOUT ====================

export interface LogoutResult {
  success: boolean
  error?: string
}

/**
 * Logout user
 */
export async function logoutUser(): Promise<LogoutResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    const { error } = await supabase.auth.signOut()

    if (error) {
      reportSupabaseAuthError(error, 'logoutUser')
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'logoutUser')
    return { success: false, error: `Logout error: ${error}` }
  }
}

// ==================== SESSION MANAGEMENT ====================

/**
 * Get current session
 */
export async function getCurrentSession(): Promise<AuthSession | null> {
  if (!supabase) return null

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session) return null

    return {
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      expiresAt: session.expires_at || 0,
      user: {
        id: session.user.id,
        email: session.user.email!,
        emailConfirmed: !!session.user.email_confirmed_at,
      },
    }
  } catch (error) {
    return null
  }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!supabase) return null

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return null

    return {
      id: user.id,
      email: user.email!,
      emailConfirmed: !!user.email_confirmed_at,
    }
  } catch (error) {
    console.error('[Auth] Failed to get user:', error)
    return null
  }
}

/**
 * Refresh session
 */
export async function refreshSession(): Promise<AuthSession | null> {
  if (!supabase) return null

  try {
    const {
      data: { session },
    } = await supabase.auth.refreshSession()

    if (!session) return null

    return {
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      expiresAt: session.expires_at || 0,
      user: {
        id: session.user.id,
        email: session.user.email!,
        emailConfirmed: !!session.user.email_confirmed_at,
      },
    }
  } catch (error) {
    console.error('[Auth] Failed to refresh session:', error)
    return null
  }
}

// ==================== AUTH STATE LISTENERS ====================

export type AuthStateListener = (user: AuthUser | null) => void

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(callback: AuthStateListener): () => void {
  if (!supabase) {
    return () => {} // No-op cleanup
  }

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event: string, session: { user: { id: string; email: string; email_confirmed_at?: string } } | null) => {
    if (session?.user) {
      callback({
        id: session.user.id,
        email: session.user.email!,
        emailConfirmed: !!session.user.email_confirmed_at,
      })
    } else if (event === 'SIGNED_OUT') {
      // Only clear user on deliberate sign-out.
      // Token refresh failures also fire with null session but should NOT
      // log the user out — the cached session may still be recoverable.
      callback(null)
    }
  })

  // Return cleanup function
  return () => {
    subscription.unsubscribe()
  }
}

// ==================== PASSWORD RESET (für später) ====================

/**
 * Request password reset email
 */
export async function requestPasswordReset(email: string, language?: string): Promise<LogoutResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  const rateLimitError = checkRateLimit('passwordReset')
  if (rateLimitError) return { success: false, error: rateLimitError }

  try {
    const platformAPI = getPlatformAPI()

    // Platform-specific redirect URLs with source parameter
    // Electron: Add ?source=electron to trigger deep link redirect
    // Web: Add ?source=web to stay in browser
    const baseRedirect = platformAPI.platform === 'electron'
      ? 'https://app.happytask.app?source=electron'
      : 'https://app.happytask.app?source=web'
    // Sprache als Query-Param für den Send-Email-Hook (Fallback wenn user_metadata leer)
    const normalizedLang = language?.toLowerCase().split('-')[0]
    const redirectTo = normalizedLang ? `${baseRedirect}&lang=${normalizedLang}` : baseRedirect

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })

    if (error) {
      reportSupabaseAuthError(error, 'requestPasswordReset')
      recordFailure('passwordReset')
      return { success: false, error: error.message }
    }

    recordSuccess('passwordReset')
    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'requestPasswordReset')
    return { success: false, error: `Password reset error: ${error}` }
  }
}

export interface UpdatePasswordResult {
  success: boolean
  error?: string
  errorKey?: string
}

function mapAuthErrorToI18nKey(error: unknown): string | null {
  const err = error as { message?: string; code?: string; name?: string }
  const code = err?.code
  const message = err?.message ?? ''
  const name = err?.name ?? ''

  if (code === 'same_password' || /different from the old password/i.test(message)) {
    return 'auth.resetPassword.errorSamePassword'
  }

  if (
    code === 'session_not_found' ||
    name === 'AuthSessionMissingError' ||
    /auth session missing/i.test(message)
  ) {
    return 'auth.resetPassword.errorSessionMissing'
  }

  // Server misconfiguration — Supabase project still using legacy JWT keys
  // after the publishable-key migration. Show a contact-support hint instead
  // of the raw "Legacy API Keys are disabled" message.
  if (/legacy api keys? (?:are |is )?disabled/i.test(message)) {
    return 'auth.errorServerConfig'
  }

  return null
}

/**
 * Update password
 */
export async function updatePassword(newPassword: string): Promise<UpdatePasswordResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    // is_active gate — covers the edge case where a deactivated user
    // clicks an OLD recovery link that was generated before the
    // send-email-hook block was deployed. The recovery session is
    // already set; refuse the password update and sign them out so
    // they don't end up with new credentials they can't use.
    const userId = (await supabase.auth.getUser?.())?.data?.user?.id
    if (userId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_active')
        .eq('id', userId)
        .maybeSingle()
      if (profile?.is_active === false) {
        await supabase.auth.signOut()
        return { success: false, error: 'Account is not available.' }
      }
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) {
      reportSupabaseAuthError(error, 'updatePassword')
      const errorKey = mapAuthErrorToI18nKey(error) ?? undefined
      return { success: false, error: error.message, errorKey }
    }

    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'updatePassword')
    const errorKey = mapAuthErrorToI18nKey(error) ?? undefined
    return { success: false, error: `Password update error: ${error}`, errorKey }
  }
}

// ==================== EMAIL VERIFICATION ====================

/**
 * Resend verification email
 */
export async function resendVerificationEmail(email: string): Promise<LogoutResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      reportSupabaseAuthError(error, 'resendVerificationEmail')
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'resendVerificationEmail')
    return { success: false, error: `Resend email error: ${error}` }
  }
}

// ==================== PROFILE UPDATE ====================

export interface UpdateProfileParams {
  fullName?: string
  password?: string
}

export interface UpdateProfileResult {
  success: boolean
  error?: string
}

/**
 * Update user profile (name and/or password after registration)
 *
 * @param params - Object with optional fullName and password
 * @returns Promise with success status and optional error message
 *
 * @example
 * // Update only name
 * await updateUserProfile({ fullName: 'John Doe' })
 *
 * // Update only password
 * await updateUserProfile({ password: 'newPassword123' })
 *
 * // Update both
 * await updateUserProfile({ fullName: 'John Doe', password: 'newPassword123' })
 */
export async function updateUserProfile(
  params: UpdateProfileParams
): Promise<UpdateProfileResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  // Validate at least one parameter is provided
  if (!params.fullName && !params.password) {
    return { success: false, error: 'At least one parameter (fullName or password) must be provided' }
  }

  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'No user found' }
    }

    // 1. Update user metadata (full_name) if provided
    if (params.fullName) {
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { full_name: params.fullName }
      })

      if (metadataError) {
        reportSupabaseAuthError(metadataError, 'updateUserProfile.metadata')
        return { success: false, error: metadataError.message }
      }

      // 2. Check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single()

      if (existingProfile) {
        // Profile exists - UPDATE
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            full_name: params.fullName,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id)

        if (profileError) {
          reportSupabaseAuthError(profileError, 'updateUserProfile.profileUpdate')
          return { success: false, error: profileError.message }
        }
      } else {
        // Profile doesn't exist - INSERT
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            full_name: params.fullName,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (profileError) {
          reportSupabaseAuthError(profileError, 'updateUserProfile.profileInsert')
          return { success: false, error: profileError.message }
        }
      }

      // Persist a device-level hint so the login screen can greet the
      // returning user by name after sign-out. localStorage survives the
      // logout wipe (Dexie does not — it's deleted wholesale).
      try {
        localStorage.setItem('lastFullName', params.fullName)
      } catch {
        /* non-fatal */
      }
    }

    // 3. Set password if provided
    if (params.password) {
      const { error: passwordError } = await supabase.auth.updateUser({
        password: params.password
      })

      if (passwordError) {
        reportSupabaseAuthError(passwordError, 'updateUserProfile.password')
        return { success: false, error: passwordError.message }
      }
    }

    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'updateUserProfile')
    return { success: false, error: `Profile update error: ${error}` }
  }
}

// ==================== OTP AUTHENTICATION ====================

/**
 * Send OTP code to email
 * Sends a 6-digit one-time password to the user's email
 * Auto-creates user if they don't exist yet
 */
export async function sendEmailOTP(email: string, language?: string): Promise<OTPResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  const rateLimitError = checkRateLimit('otp')
  if (rateLimitError) return { success: false, error: rateLimitError }

  // i18next liefert volle Locales ("de-DE"); wir speichern nur den Base-Code
  const normalizedLang = language?.toLowerCase().split('-')[0]

  // Sprache als Query-Param in redirect_to — wird vom Send-Email-Hook gelesen,
  // weil user_metadata bei der allerersten Signup-Mail noch leer ist
  // (Supabase persistiert options.data erst bei verifyOtp).
  // Hinweis: Mit aktivem Custom-Email-Hook ist emailRedirectTo harmlos und
  // schaltet nicht auf Magic-Link-Mode um, weil der Hook das Template selbst rendert.
  const redirectTo = normalizedLang
    ? `${window.location.origin}/auth/callback?lang=${normalizedLang}`
    : undefined

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true, // Auto-register if user doesn't exist
        ...(redirectTo ? { emailRedirectTo: redirectTo } : {}),
        ...(normalizedLang ? { data: { language: normalizedLang } } : {}),
      },
    })

    if (error) {
      reportSupabaseAuthError(error, 'sendEmailOTP')
      recordFailure('otp')
      return { success: false, error: error.message }
    }

    recordSuccess('otp')
    return { success: true }
  } catch (error) {
    reportSupabaseAuthError(error, 'sendEmailOTP')
    return { success: false, error: `OTP send error: ${error}` }
  }
}

/**
 * Verify OTP code
 * Validates the 6-digit code sent to the user's email
 * Returns user and session on success
 */
export async function verifyEmailOTP(
  email: string,
  token: string,
): Promise<VerifyOTPResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (error) {
      reportSupabaseAuthError(error, 'verifyEmailOTP')
      return { success: false, error: error.message }
    }

    if (!data.user || !data.session) {
      return { success: false, error: 'Verification failed' }
    }

    return {
      success: true,
      user: {
        id: data.user.id,
        email: data.user.email!,
        emailConfirmed: true, // OTP means email is confirmed
      },
      session: {
        accessToken: data.session.access_token,
        refreshToken: data.session.refresh_token,
        expiresAt: data.session.expires_at || 0,
        user: {
          id: data.user.id,
          email: data.user.email!,
          emailConfirmed: true,
        },
      },
    }
  } catch (error) {
    reportSupabaseAuthError(error, 'verifyEmailOTP')
    return { success: false, error: `OTP verify error: ${error}` }
  }
}

// ==================== USER METADATA ====================

/**
 * Ensure user_metadata.language matches the given language.
 * No-op if already correct. Used after OAuth login and after OTP verification
 * to cover edge cases where the app language differs from stored metadata.
 * Tolerant to errors — a failed metadata update must not break the login flow.
 */
export async function syncUserLanguage(language: string): Promise<void> {
  if (!supabase) return
  const normalized = language.toLowerCase().split('-')[0]
  if (!normalized) return
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return
    const current = (user.user_metadata as { language?: string } | null)?.language
    if (current === normalized) return
    await supabase.auth.updateUser({ data: { language: normalized } })
  } catch (err) {
    console.warn('[Auth] syncUserLanguage failed:', err)
  }
}
